/*
  PainLogPanel — inline panel inside the ExerciseProgramEditor for
  logging a client's pre/post-session pain score (0-10) over time.

  Rendered inside the editor's left column just under the
  "Precautions / pathologies" block. Only shows the log + chart when
  the program has `pain_tracking_enabled` on; otherwise collapses to
  a one-line "Enable pain tracking" toggle so it doesn't clutter
  programs that don't need it.

  API contract (see /app/backend/routes/exercise_programs.py):
    POST   /api/exercise-programs/{id}/pain-log            → add
    DELETE /api/exercise-programs/{id}/pain-log/{entry_id} → remove
  Both endpoints return the refreshed program doc so we replace state
  in place — no full reload flicker.
*/
import { todayISO as melToday } from "@/lib/intl";
import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Activity, Plus, Trash2, Loader2 } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

export default function PainLogPanel({ programId, doc, locked, onToggle, onDocRefresh }) {
  const enabled = !!doc?.pain_tracking_enabled;
  const entries = useMemo(() => {
    const arr = [...((doc?.pain_log) || [])];
    arr.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    return arr;
  }, [doc?.pain_log]);
  const [logOpen, setLogOpen] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  // Toggling tracking is operational metadata (see backend
  // OPERATIONAL_META), so we allow it on ANY status — even locked
  // programs — and persist immediately so the flag isn't lost if the
  // operator forgets to hit Save.
  async function toggleTracking(next) {
    // Optimistic UI update first so the checkbox feels instant.
    onToggle?.(next);
    setSavingToggle(true);
    try {
      const r = await api.patch(`/exercise-programs/${programId}`, {
        pain_tracking_enabled: next,
      });
      onDocRefresh?.(r.data);
    } catch (e) {
      toast.error("Couldn't update pain tracking", {
        description: e?.response?.data?.detail || e.message,
      });
      onToggle?.(!next);  // revert
    } finally { setSavingToggle(false); }
  }

  async function removeEntry(id) {
    if (!window.confirm("Delete this pain-log entry?")) return;
    try {
      const r = await api.delete(`/exercise-programs/${programId}/pain-log/${id}`);
      onDocRefresh?.(r.data);
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  // ---------- summary numbers (average pre/post + Δ across last N) ----------
  const summary = useMemo(() => {
    if (!entries.length) return null;
    const last = entries.slice(-5);
    const avg = (arr, k) => arr.reduce((s, x) => s + (Number(x[k]) || 0), 0) / arr.length;
    return {
      count: entries.length,
      avgPre: avg(entries, "pre"),
      avgPost: avg(entries, "post"),
      avgDelta: avg(entries, "post") - avg(entries, "pre"),
      recentDelta: avg(last, "post") - avg(last, "pre"),
      recentN: last.length,
    };
  }, [entries]);

  return (
    <Card className="p-3 rounded-lg border border-zinc-200 shadow-none space-y-3"
          data-testid="ep-pain-log-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-rose-50 border border-rose-200 text-rose-800">
            <Activity className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-600 font-semibold">
              Pain scores (0-10) — pre &amp; post session
            </div>
            <div className="text-2xs text-zinc-500">
              {enabled
                ? "Log the client's pain rating at the start and end of every session to track progress over time."
                : "Enable this for programs where you want to track pain progression (e.g. lower-back, chronic pain, post-op)."}
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-700 select-none whitespace-nowrap"
               data-testid="ep-pain-tracking-toggle">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-indigo-600"
            checked={enabled}
            disabled={savingToggle}
            onChange={(e) => toggleTracking(e.target.checked)}
          />
          Track this program
          {savingToggle && <Loader2 className="h-3 w-3 animate-spin text-zinc-400" />}
        </label>
      </div>

      {enabled && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setLogOpen(true)} data-testid="ep-pain-log-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Log session
            </Button>
            {summary && (
              <div className="text-2xs text-zinc-600 font-mono-num">
                {summary.count} session{summary.count !== 1 ? "s" : ""}
                {" · "}avg pre {summary.avgPre.toFixed(1)}
                {" · "}avg post {summary.avgPost.toFixed(1)}
                {" · "}avg Δ {(summary.avgDelta >= 0 ? "+" : "") + summary.avgDelta.toFixed(1)}
                {summary.recentN >= 2 && (
                  <span className="text-zinc-500">
                    {" "}(last {summary.recentN}: {(summary.recentDelta >= 0 ? "+" : "") + summary.recentDelta.toFixed(1)})
                  </span>
                )}
              </div>
            )}
          </div>

          {entries.length > 0 ? (
            <>
              <div className="h-40 w-full" data-testid="ep-pain-log-chart">
                <ResponsiveContainer>
                  <LineChart data={entries.map((x) => ({
                    date: (x.date || "").slice(5),  // MM-DD is enough on the axis
                    Pre: x.pre,
                    Post: x.post,
                  }))} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="Pre" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Post" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="ep-pain-log-table">
                  <thead>
                    <tr className="text-left text-2xs uppercase text-zinc-500 border-b border-zinc-200">
                      <th className="py-1 pr-2">Date</th>
                      <th className="py-1 pr-2 text-center w-14">Pre</th>
                      <th className="py-1 pr-2 text-center w-14">Post</th>
                      <th className="py-1 pr-2 text-center w-14">Δ</th>
                      <th className="py-1 pr-2">Note</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...entries].reverse().map((x) => {
                      const delta = (x.post || 0) - (x.pre || 0);
                      return (
                        <tr key={x.id} className="border-b border-zinc-100">
                          <td className="py-1 pr-2 font-mono-num">{fmtDate(x.date)}</td>
                          <td className="py-1 pr-2 text-center font-medium text-rose-700">{x.pre}</td>
                          <td className="py-1 pr-2 text-center font-medium text-emerald-700">{x.post}</td>
                          <td className={"py-1 pr-2 text-center font-mono-num " + (delta < 0 ? "text-emerald-700" : delta > 0 ? "text-rose-700" : "text-zinc-500")}>
                            {delta === 0 ? "0" : (delta > 0 ? `+${delta}` : `${delta}`)}
                          </td>
                          <td className="py-1 pr-2 text-zinc-600">{x.note || ""}</td>
                          <td className="py-1">
                            {!locked && (
                              <button
                                onClick={() => removeEntry(x.id)}
                                title="Delete entry"
                                className="p-1 rounded hover:bg-red-50 text-red-600"
                                data-testid={`ep-pain-log-del-${x.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded p-3">
              No sessions logged yet. Click <b>Log session</b> to record the first pre/post score.
            </div>
          )}
        </>
      )}

      <LogSessionDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        programId={programId}
        onSaved={(fresh) => { onDocRefresh?.(fresh); setLogOpen(false); }}
      />
    </Card>
  );
}


function LogSessionDialog({ open, onOpenChange, programId, onSaved }) {
  const today = melToday();
  const [date, setDate] = useState(today);
  const [pre, setPre] = useState(5);
  const [post, setPost] = useState(5);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset form each time the dialog opens so a second "Log session"
  // click doesn't come back pre-filled with the previous entry.
  function reset() {
    setDate(today);
    setPre(5);
    setPost(5);
    setNote("");
  }

  async function submit() {
    setSaving(true);
    try {
      const r = await api.post(`/exercise-programs/${programId}/pain-log`, {
        date, pre: Number(pre), post: Number(post),
        note: note.trim() || undefined,
      });
      toast.success("Session logged");
      reset();
      onSaved?.(r.data);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="ep-pain-log-dialog">
        <DialogHeader>
          <DialogTitle>Log pain session</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-2xs uppercase text-zinc-500">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   data-testid="ep-pain-log-date" />
          </div>
          <PainSlider label="Pain before session" testid="ep-pain-log-pre"
                      value={pre} onChange={setPre} tone="rose" />
          <PainSlider label="Pain after session" testid="ep-pain-log-post"
                      value={post} onChange={setPost} tone="emerald" />
          <div>
            <label className="text-2xs uppercase text-zinc-500">Note (optional)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Felt fine warming up, tight in the L4-L5 by end"
                      data-testid="ep-pain-log-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ep-pain-log-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Save session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PainSlider({ label, value, onChange, tone, testid }) {
  const toneClass = tone === "rose" ? "accent-rose-600 text-rose-700"
                                    : "accent-emerald-600 text-emerald-700";
  return (
    <div>
      <div className="flex items-center justify-between text-2xs uppercase text-zinc-500">
        <span>{label}</span>
        <span className={`text-sm font-semibold ${toneClass}`}>{value} / 10</span>
      </div>
      <input
        type="range"
        min="0" max="10" step="1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`w-full ${toneClass}`}
        data-testid={testid}
      />
      <div className="flex justify-between text-2xs text-zinc-400 mt-0.5 font-mono-num">
        <span>0 · No pain</span><span>5</span><span>10 · Worst</span>
      </div>
    </div>
  );
}
